import {
    Grid, GridItem
} from '@chakra-ui/react';
import {
    t, Trans
} from '@lingui/macro';
import {
    Form, Field, Formik, FieldProps
} from 'formik';
import React, {
    useState, ChangeEvent
} from 'react';
import { useDispatch } from 'react-redux';

import {
    FormSelect, FormInput
} from '../../../../../../../components/form';
import { useSelectorIsInternalUsers } from '../../../../../../../user-profiles';
import {
    useSelectedProfilePicture, selectProfilePicture
} from '../../../profile-picture-upload-modal';
import { ProfilePicture }from './components/profile-picture/profile-picture';
import { useSelectorUserProfile } from './user-profile-selectors';
import {
    CloseButton, UserProfileWrapper,
    CloseButtonWrapper, Header, UserDetails,
    Username, SaveButton, Footer, UserHeader
} from './user-profile.styled';
import {
    ProfileFormType, UserFields
} from './user-profile.types';
import {
    handleCloseModal,
    getValidationSchema,
    getInitialValues,
    getTitleValues,
    isSubmitButtonDisabled,
    onSubmitHandler
} from './user-profile.utils';

export function UserProfile () {
    const dispatch = useDispatch();
    const [hasEmailChanged, setHasEmailChanged] = useState(false);
    const userDetails = useSelectorUserProfile();
    const [profileImage, setProfileImage] = useState(userDetails?.profileImage || '');
    const [hasProfileImageChanged, setHasProfileImageChanged] = useState(false);
    const isInternalUser = useSelectorIsInternalUsers();
    const selectedProfilePicture = useSelectedProfilePicture();
    const handleRemoveProfileImage = () => {
        setHasProfileImageChanged(true);
        setProfileImage('');
        dispatch(selectProfilePicture(''));
    };

    return (
        <UserProfileWrapper>
            <Formik
                initialValues={getInitialValues(userDetails)}
                onSubmit={(values: ProfileFormType) =>
                    onSubmitHandler(values, selectedProfilePicture || profileImage, dispatch, hasEmailChanged)}
                validationSchema={getValidationSchema()}
            >
                {(props) => (
                    <Form>
                        <CloseButtonWrapper>
                            <CloseButton
                                data-testid={'close-user-modal'}
                                onClick={() => handleCloseModal(dispatch, props.dirty)}
                            >
                                X
                            </CloseButton>
                        </CloseButtonWrapper>
                        <Header>
                            <ProfilePicture
                                profileImage={selectedProfilePicture || profileImage}
                                handleRemoveProfileImage={handleRemoveProfileImage}
                            />
                            <UserDetails>
                                <UserHeader>
                                    <Trans id={'header.myprofile'} />
                                </UserHeader>
                                <Username data-testid={'user-modal-username'}>
                                    {t`profile.username`} : {userDetails?.username}
                                </Username>
                            </UserDetails>
                        </Header>
                        <Grid templateColumns={'repeat(2, 1fr)'} gap={10} minHeight={71}>
                            <GridItem w={'50%'}>
                                <Field>
                                    {({
                                        field, form
                                    } : FieldProps) => (
                                        <FormSelect
                                            data-testid={`user-modal-${UserFields.title}`}
                                            errors={form.errors}
                                            isRequired={false}
                                            labelText={t`profile.title`}
                                            name={UserFields.title}
                                            onChangeHandler={form.handleChange}
                                            options={getTitleValues()}
                                            value={field.value[UserFields.title]}
                                            isReadOnly={isInternalUser}
                                        />
                                    )}
                                </Field>
                            </GridItem>
                            <GridItem w={'100%'}>
                                <Field>
                                    {({
                                        field, form
                                    }: FieldProps) => (
                                        <FormInput
                                            data-testid={`user-modal-${UserFields.firstName}`}
                                            errors={form.errors}
                                            isRequired={true}
                                            labelText={t`firstname`}
                                            name={UserFields.firstName}
                                            onChangeHandler={field.onChange}
                                            type={'text'}
                                            value={field.value[UserFields.firstName]}
                                            isReadOnly={isInternalUser}
                                        />
                                    )}
                                </Field>
                            </GridItem>
                        </Grid>
                        <Grid templateColumns={'repeat(1, 1fr)'} mt={3} minHeight={420}>
                            <GridItem w={'100%'}>
                                <Field>
                                    {({
                                        field, form
                                    }: FieldProps) => (
                                        <FormInput
                                            data-testid={`user-modal-${UserFields.lastname}`}
                                            errors={form.errors}
                                            isRequired={true}
                                            labelText={t`lastname`}
                                            name={UserFields.lastname}
                                            onChangeHandler={field.onChange}
                                            type={'text'}
                                            value={field.value[UserFields.lastname]}
                                            isReadOnly={isInternalUser}
                                        />
                                    )}
                                </Field>
                            </GridItem>
                            <GridItem w={'100%'}>
                                <Field>
                                    {({
                                        field, form
                                    }: FieldProps) => (
                                        <FormInput
                                            data-testid={`user-modal-${UserFields.email}`}
                                            errors={form.errors}
                                            isRequired={true}
                                            labelText={t`email`}
                                            name={UserFields.email}
                                            onChangeHandler={(e: ChangeEvent) => {
                                                field.onChange(e);
                                                setHasEmailChanged(field.value[UserFields.email] === userDetails?.email);
                                            }}
                                            type={'email'}
                                            value={field.value[UserFields.email]}
                                            isReadOnly={isInternalUser}
                                        />
                                    )}
                                </Field>
                            </GridItem>
                            <GridItem w={'100%'}>
                                <Field>
                                    {({
                                        field, form
                                    }: FieldProps) => (
                                        <FormInput
                                            data-testid={`user-modal-${UserFields.currentPassword}`}
                                            errors={form.errors}
                                            labelText={t`currentPassword`}
                                            name={UserFields.currentPassword}
                                            onChangeHandler={field.onChange}
                                            type={'password'}
                                            value={field.value[UserFields.currentPassword]}
                                            isReadOnly={isInternalUser}
                                        />
                                    )}
                                </Field>
                            </GridItem>
                            <GridItem w={'100%'}>
                                <Field>
                                    {({
                                        field, form
                                    }: FieldProps) => (
                                        <FormInput
                                            data-testid={`user-modal-${UserFields.newPassword}`}
                                            errors={form.errors}
                                            labelText={t`newPassword`}
                                            name={UserFields.newPassword}
                                            onChangeHandler={field.onChange}
                                            type={'password'}
                                            value={field.value[UserFields.newPassword]}
                                            isReadOnly={isInternalUser}
                                        />
                                    )}
                                </Field>
                            </GridItem>
                            <GridItem w={'100%'}>
                                <Field>
                                    {({
                                        field, form
                                    }: FieldProps) => (
                                        <FormInput
                                            data-testid={`user-modal-${UserFields.confirmPassword}`}
                                            errors={form.errors}
                                            labelText={t`confirmPassword`}
                                            name={UserFields.confirmPassword}
                                            onChangeHandler={field.onChange}
                                            type={'password'}
                                            value={field.value[UserFields.confirmPassword]}
                                            isReadOnly={isInternalUser}
                                        />
                                    )}
                                </Field>
                            </GridItem>
                        </Grid>
                        {!isInternalUser && (
                            <>
                                <Footer>
                                    <SaveButton
                                        type={'submit'}
                                        data-testid={'save-btn'}
                                        disabled={isSubmitButtonDisabled(props, selectedProfilePicture, hasProfileImageChanged)}
                                    >
                                        <Trans id={'button.save'} />
                                    </SaveButton>
                                </Footer>
                                <Footer>
                                    * {t`profile.requiredFields`}
                                </Footer>
                            </>
                        )}
                    </Form>
                )}
            </Formik>
        </UserProfileWrapper>
    );
}
